"use client";

import { useState, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  Mail,
  Phone,
  MapPin,
  CheckCircle,
  MessageSquare
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';
import dynamic from 'next/dynamic';
import { useLanguage } from '@/contexts/language-context';
import { BookingForm } from '@/components/BookingForm';
import { BookingConfig, FormSection } from '@/types/booking';

interface ContactInfo {
  icon: any;
  title: string;
  content: string;
  description: string;
  note?: string;
}

const AnimatedBackground = dynamic(() => import('@/components/animated-background'), { ssr: false });

const ContactPage = () => {
  const { t, language } = useLanguage();

  const contactInfo: ContactInfo[] = [
    {
      icon: Mail,
      title: t('contact.emailUs'),
      content: "info@agnuscloud.com",
      description: t('contact.emailDescription')
    },
    {
      icon: Phone,
      title: t('contact.callUs'),
      content: "+31 85 - 488 354 3",
      description: t('contact.callDescription')
    },
    {
      icon: MapPin,
      title: t('contact.visitUs'),
      content: "Hoenderberg 28, 3825CR Amersfoort, Utrecht Nederland",
      description: t('contact.visitDescription'),
      note: t('contact.noVisitingAddress')
    }
  ];

  // Configure the BookingForm to match website styling
  const bookingConfig: BookingConfig = useMemo(() => ({
    title: t('contact.sendMessage'),
    description: t('contact.sendMessageSubtitle'),
    heroImage: undefined,
    currency: '€',
    timezone: 'Europe/Amsterdam',
    layout: 'wizard',
    darkMode: true,
    
    // Date/time constraints
    minAdvanceHours: 2, // Minimum 2 hours advance booking
    maxAdvanceDays: 60, // Maximum 60 days in advance
    blackoutDates: [], // No blackout dates for now
    
    typography: {
      fontFamily: 'Poppins, sans-serif',
    },
    
    theme: {
      primary: '#8785B9', // cloud-blue
      secondary: '#C5B6D1', // cloud-lavender
      background: 'rgba(44, 39, 82, 0.6)', // deep-purple with opacity
      text: 'rgba(253, 246, 238, 0.95)', // cream
      textSecondary: 'rgba(253, 246, 238, 0.7)', // cream with opacity
      border: 'rgba(197, 182, 209, 0.3)', // cloud-lavender with opacity
      success: '#10B981',
      error: '#EF4444',
    },
    
    features: {
      multiStep: true,
      calendar: true,
      timeSlots: true,
      videoCall: true,
      pricing: false,
      customFields: false,
      progressIndicator: true,
    },
    
    fields: {
      name: true,
      email: true,
      phone: true,
      message: true,
      meetingType: true,
      videoCallPlatform: false,
    },
    
    sections: [
      {
        id: 'hero',
        name: language === 'nl' ? 'Kennismaking gesprek' : 'Introduction Meeting',
        title: language === 'nl' ? 'Kennismaking gesprek' : 'Introduction Meeting',
        description: language === 'nl' ? 'Plan een kennismakingsgesprek met ons team' : 'Schedule an introduction meeting with our team',
        enabled: true,
        order: 1,
        fields: ['hero']
      },
      {
        id: 'scheduling',
        name: language === 'nl' ? 'Plan je dienst' : 'Schedule Service',
        title: language === 'nl' ? 'Plan je dienst' : 'Schedule Service',
        description: language === 'nl' ? 'Bekijk onze beschikbaarheid en boek de datum, tijd en type afspraak die jou goed uitkomen' : 'View our availability and book the date, time and type of appointment that suits you',
        enabled: true,
        order: 2,
        fields: ['date', 'time', 'meetingType']
      },
      {
        id: 'contact',
        name: language === 'nl' ? 'Klantgegevens' : 'Client Information',
        title: language === 'nl' ? 'Klantgegevens' : 'Client Information',
        description: language === 'nl' ? 'Vertel ons wat meer over jezelf' : 'Tell us more about yourself',
        enabled: true,
        order: 3,
        fields: ['name', 'email', 'phone', 'message']
      }
    ] as FormSection[],
    
    timeSlots: {
      enabled: true,
      duration: 60,
      buffer: 15,
      availability: {
        mon: { 
          start: '09:00', 
          end: '17:00', 
          slots: ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'] 
        },
        tue: { 
          start: '09:00', 
          end: '17:00', 
          slots: ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'] 
        },
        wed: { 
          start: '09:00', 
          end: '17:00', 
          slots: ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'] 
        },
        thu: { 
          start: '09:00', 
          end: '17:00', 
          slots: ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'] 
        },
        fri: { 
          start: '09:00', 
          end: '17:00', 
          slots: ['09:00', '10:00', '11:00', '12:00', '13:00', '14:00', '15:00', '16:00', '17:00', '18:00', '19:00', '20:00'] 
        },
      },
    },
    
    videoCall: {
      platforms: [
        { id: 'teams', name: 'Microsoft Teams', icon: '💼', enabled: true },
        { id: 'zoom', name: 'Zoom', icon: '📹', enabled: true },
        { id: 'meet', name: 'Google Meet', icon: '🎥', enabled: true },
        { id: 'other', name: 'Other', icon: '📞', enabled: true },
      ]
    },
    
    pricing: {
      enabled: false,
      options: []
    },
    
    customFields: [],
    
    submission: {
      endpoint: '#',
      method: 'POST',
      successMessage: t('contact.success.description'),
      errorMessage: t('contact.error.description'),
    },
    
          localization: {
        language: language,
        translations: language === 'nl' ? {
          'field.name': 'Voornaam',
          'field.email': 'E-mailadres',
          'field.phone': 'Telefoonnummer',
          'field.message': 'Bericht',
          'field.date': 'Selecteer een datum en tijd',
          'field.time': 'Selecteer tijd',
          'field.meetingType': 'Type afspraak',
          'meetingType.inPerson': 'Op locatie',
          'meetingType.online': 'Online gesprek',
          'validation.nameRequired': 'Naam is verplicht',
          'validation.emailRequired': 'E-mailadres is verplicht',
          'validation.emailInvalid': 'E-mailadres is ongeldig',
          'validation.phoneRequired': 'Telefoonnummer is verplicht',
          'validation.dateRequired': 'Datum is verplicht',
          'validation.timeRequired': 'Tijd is verplicht',
          'validation.meetingTypeRequired': 'Type afspraak is verplicht',
          'success.title': 'Bedankt!',
          'success.message': 'Je zult een e-mailbevestiging ontvangen.',
          'button.bookNow': 'Nu boeken',
          'button.next': 'Volgende',
          'button.previous': 'Terug',
          'button.submit': 'Nu boeken',
          'booking.planYourCall': 'Plan je gesprek',
          'booking.freeConsultation': 'Gratis kennismakingsgesprek',
          'booking.personalizedAdvice': 'Persoonlijk advies op maat',
          'booking.onlineOrOnsite': 'Online of op locatie',
          'booking.stepOf': 'Stap {current} van {total}',
          'datePicker.selectDate': 'Selecteer een datum',
          'datePicker.selectTime': 'Selecteer een tijd',
          'datePicker.selectDateFirst': 'Selecteer eerst een datum',
          'datePicker.noAvailableTime': 'Geen beschikbare tijden voor deze datum',
          'datePicker.pageInfo': '{current} van {total}',
          'field.messagePlaceholder': 'Vertel ons meer over je behoeften...',
          'field.namePlaceholder': 'Voer je volledige naam in',
          'field.emailPlaceholder': 'je@email.com',
          'field.phonePlaceholder': '+31 6 12345678',
          'toast.saving': 'Afspraak wordt opgeslagen...',
          'toast.pleaseWait': 'Een moment geduld alstublieft.',
          'toast.saved': '✅ Afspraak opgeslagen!',
          'toast.sendingEmail': 'Bevestigingsmail wordt verzonden...',
          'toast.emailSent': '🎉 Bevestigingsmail verzonden!',
          'toast.emailSentTo': 'Een bevestigingsmail is verzonden naar {email}',
          'toast.emailWarning': '⚠️ E-mail waarschuwing',
          'toast.emailFailed': 'Afspraak opgeslagen, maar bevestigingsmail kon niet worden verzonden.',
          'toast.error': '❌ Fout bij het opslaan',
          'toast.errorMessage': 'Er ging iets mis bij het opslaan van uw afspraak. Probeer het opnieuw.',
        } : {
          'field.name': 'First Name',
          'field.email': 'Email Address',
          'field.phone': 'Phone Number',
          'field.message': 'Message',
          'field.date': 'Select a date and time',
          'field.time': 'Select time',
          'field.meetingType': 'Meeting Type',
          'meetingType.inPerson': 'In Person',
          'meetingType.online': 'Online Meeting',
          'validation.nameRequired': 'Name is required',
          'validation.emailRequired': 'Email address is required',
          'validation.emailInvalid': 'Email address is invalid',
          'validation.phoneRequired': 'Phone number is required',
          'validation.dateRequired': 'Date is required',
          'validation.timeRequired': 'Time is required',
          'validation.meetingTypeRequired': 'Meeting type is required',
          'success.title': 'Thank you!',
          'success.message': 'You will receive an email confirmation.',
          'button.bookNow': 'Book Now',
          'button.next': 'Next',
          'button.previous': 'Back',
          'button.submit': 'Book Now',
          'booking.planYourCall': 'Plan your call',
          'booking.freeConsultation': 'Free consultation meeting',
          'booking.personalizedAdvice': 'Personalized advice tailored to you',
          'booking.onlineOrOnsite': 'Online or on-site',
          'booking.stepOf': 'Step {current} of {total}',
          'datePicker.selectDate': 'Select a date',
          'datePicker.selectTime': 'Select a time',
          'datePicker.selectDateFirst': 'Select a date first',
          'datePicker.noAvailableTime': 'No available times for this date',
          'datePicker.pageInfo': '{current} of {total}',
          'field.messagePlaceholder': 'Tell us more about your needs...',
          'field.namePlaceholder': 'Enter your full name',
          'field.emailPlaceholder': 'you@email.com',
          'field.phonePlaceholder': '+1 234 567 8900',
          'toast.saving': 'Saving appointment...',
          'toast.pleaseWait': 'Please wait a moment.',
          'toast.saved': '✅ Appointment saved!',
          'toast.sendingEmail': 'Sending confirmation email...',
          'toast.emailSent': '🎉 Confirmation email sent!',
          'toast.emailSentTo': 'A confirmation email has been sent to {email}',
          'toast.emailWarning': '⚠️ Email warning',
          'toast.emailFailed': 'Appointment saved, but confirmation email could not be sent.',
          'toast.error': '❌ Error saving',
          'toast.errorMessage': 'Something went wrong while saving your appointment. Please try again.',
        }
      },
    
    spacing: {
      padding: '1rem',
      margin: '0.5rem',
    },
    
    businessHours: {
      mon: { enabled: true, start: '09:00', end: '17:00' },
      tue: { enabled: true, start: '09:00', end: '17:00' },
      wed: { enabled: true, start: '09:00', end: '17:00' },
      thu: { enabled: true, start: '09:00', end: '17:00' },
      fri: { enabled: true, start: '09:00', end: '17:00' },
      sat: { enabled: false },
      sun: { enabled: false },
    }
  }), [language, t]);

  const handleSubmissionSuccess = (data: any) => {
    console.log('Form submitted successfully:', data);
    toast.success(t('contact.success.title'), {
      description: t('contact.success.description'),
    });
  };

  const handleSubmissionError = (error: Error) => {
    console.error('Form submission error:', error);
    toast.error(t('contact.error.title'), {
      description: t('contact.error.description'),
    });
  };

  return (
    <div className="relative min-h-screen">
      <AnimatedBackground />

      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-4xl md:text-6xl font-bold text-deep-purple dark:text-cream mb-6"
          >
            {t('contact.title')}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="text-xl text-deep-purple/80 dark:text-cream/80 max-w-3xl mx-auto mb-8"
          >
            {t('contact.subtitle')}
          </motion.p>
        </div>
      </section>

      {/* Booking Form Section */}
      <section className="relative py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-cloud-blue/5 to-cloud-lavender/5">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-deep-purple dark:text-cream mb-4">
              {t('contact.sendMessage')}
            </h2>
            <p className="text-lg text-deep-purple/70 dark:text-cream/70">
              {t('contact.sendMessageSubtitle')}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <Card className="bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30">
              <CardHeader>
                <CardTitle className="text-2xl text-deep-purple dark:text-cream flex items-center gap-2">
                  <MessageSquare className="w-6 h-6 text-cloud-blue" />
                  {t('contact.contactForm')}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <BookingForm
                  config={bookingConfig}
                  onSubmissionSuccess={handleSubmissionSuccess}
                  onSubmissionError={handleSubmissionError}
                  className="space-y-6"
                />
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Response Time Info */}
      <section className="relative py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <Card className="bg-gradient-to-r from-cloud-blue/10 to-cloud-lavender/10 border-cloud-lavender/30">
              <CardContent className="p-8">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <CheckCircle className="w-8 h-8 text-green-500" />
                  <h3 className="text-2xl font-bold text-deep-purple dark:text-cream">
                    {t('contact.quickResponse')}
                  </h3>
                </div>
                <p className="text-lg text-deep-purple/70 dark:text-cream/70 mb-4">
                  {t('contact.quickResponseText')}
                </p>
                <p className="text-deep-purple/60 dark:text-cream/60">
                  {t('contact.urgentMatters')}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="relative py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {contactInfo.map((info, index) => (
              <motion.div
                key={info.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                whileHover={{ y: -10, scale: 1.02 }}
              >
                <Card className="h-full bg-white/60 dark:bg-deep-purple/60 backdrop-blur-sm border-cloud-lavender/30 hover:border-cloud-blue/50 transition-all duration-300 hover:shadow-xl">
                  <CardContent className="p-6 text-center">
                    <motion.div
                      whileHover={{ scale: 1.2, rotate: 5 }}
                      transition={{ duration: 0.3 }}
                      className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-cloud-blue to-cloud-lavender mb-4"
                    >
                      <info.icon className="w-8 h-8 text-white" />
                    </motion.div>
                    <h3 className="text-xl font-semibold text-deep-purple dark:text-cream mb-2">
                      {info.title}
                    </h3>
                    <p className="text-lg font-medium text-cloud-blue mb-1">
                      {info.content}
                    </p>
                    <p className="text-sm text-deep-purple/70 dark:text-cream/70">
                      {info.description}
                    </p>
                    {info.note && (
                      <p className="text-xs text-deep-purple/60 dark:text-cream/60 mt-1">
                        {info.note}
                      </p>
                    )}
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;